import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { sendEmailOtp, verifyEmailOtp } from "../../../services/publicService";
import logoFull from "../../../assets/images/cricket-logo.png";

const EmailVerification = () => {
  const [searchParams] = useSearchParams();

  const queryEmail = searchParams.get("email");
  const queryOtp = searchParams.get("otp");

  const [email, setEmail] = useState(queryEmail || "");
  const [otp, setOtp] = useState("");

  const [step, setStep] = useState(queryEmail ? "otp" : "email");

  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const [seconds, setSeconds] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const getApiMessage = (error, fallback) => {
    return (
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  };

  //---------------------------------------
  // Timer
  //---------------------------------------

  useEffect(() => {
    if (!canResend && step === "otp") {
      if (seconds <= 0) {
        setCanResend(true);
        return;
      }

      const timer = setTimeout(() => {
        setSeconds((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [seconds, canResend, step]);

  //---------------------------------------
  // Send OTP
  //---------------------------------------

  const handleSendOtp = async (emailAddress = email) => {
    if (!emailAddress) {
      Swal.fire("Error", "Please enter email.", "error");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: emailAddress,
      };

      const res = await sendEmailOtp(payload);

      Swal.fire("Success", "OTP sent successfully.", "success");

      setStep("otp");
      setSeconds(60);
      setCanResend(false);
    } catch (error) {
      Swal.fire("Error", getApiMessage(error, "Unable to send OTP."), "error");
    } finally {
      setLoading(false);
    }
  };

  //---------------------------------------
  // Resend OTP
  //---------------------------------------

  const handleResendOtp = async () => {
    setLoading(true);

    try {
      const payload = {
        email,
      };

      const res = await sendEmailOtp(payload);

      Swal.fire("Success", "OTP resent successfully.", "success");

      setSeconds(60);
      setCanResend(false);
    } catch (error) {
      Swal.fire(
        "Error",
        getApiMessage(error, "Unable to resend OTP."),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  //---------------------------------------
  // Verify OTP
  //---------------------------------------

  const handleVerifyOtp = async (otpValue = otp) => {
    if (!email) {
      setVerificationError("Email is required.");
      Swal.fire("Error", "Email is required.", "error");
      return;
    }

    if (!otpValue) {
      setVerificationError("Please enter OTP.");
      Swal.fire("Error", "Please enter OTP.", "error");
      return;
    }

    setLoading(true);
    setVerificationError("");

    try {
      const payload = {
        email,
        otp: otpValue,
      };

      await verifyEmailOtp(payload);

      setVerified(true);
    } catch (error) {
      const message = getApiMessage(error, "OTP verification failed.");
      setVerificationError(message);
      Swal.fire("Error", message, "error");
    } finally {
      setLoading(false);
    }
  };

  //---------------------------------------
  // Auto Flow
  //---------------------------------------

  useEffect(() => {
    if (!queryEmail) return;

    setEmail(queryEmail);

    // email + otp
    if (queryEmail && queryOtp) {
      setStep("verifying");
      handleVerifyOtp(queryOtp);
      return;
    }

    // email only
    if (queryEmail) {
      setStep("otp");
      handleSendOtp(queryEmail);
    }
  }, [queryEmail, queryOtp]);

  const showVerificationErrorState =
    step === "verifying" && !loading && !!verificationError;

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "100vh", background: "#f8f9fa" }}
    >
      {showVerificationErrorState ? (
        <div className="text-center px-4" style={{ maxWidth: 420 }}>
          <h4 className="mb-2 text-danger">Verification Failed</h4>
          <p className="text-danger mb-3">{verificationError}</p>
          <button
            className="btn btn-primary"
            onClick={() => (window.location.href = "/login")}
          >
            Go To Login
          </button>
        </div>
      ) : (
        <div
          className="card shadow-lg border-0"
          style={{ width: "100%", maxWidth: 500, borderRadius: 15 }}
        >
          <div className="card-body p-5">
            <div className="text-center mb-3">
              <Link to="/login">
                <img src={logoFull} alt="logo" width="150" height="140" />
              </Link>
            </div>
            {!verified ? (
              <div className="text-center px-4" style={{ maxWidth: 420 }}>
                {step === "verifying" && loading && (
                  <>
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    />
                    <h4 className="mb-2">Verifying your email</h4>
                    <p className="text-muted mb-3">
                      Please wait while we verify your email.
                    </p>
                  </>
                )}

                {step === "email" && (
                  <>
                    <h4 className="mb-3">Email Verification</h4>
                    <p className="text-muted mb-3">
                      Enter your email to receive an OTP.
                    </p>
                    <input
                      type="email"
                      className="form-control mb-3"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <button
                      className="btn btn-primary w-100"
                      onClick={() => handleSendOtp()}
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send OTP"}
                    </button>
                  </>
                )}

                {step === "otp" && (
                  <>
                    <h4 className="mb-3">Enter OTP</h4>
                    <p className="text-muted mb-3">
                      We sent a code to your email address.
                    </p>
                    <input
                      className="form-control mb-3"
                      value={email}
                      disabled
                    />
                    <input
                      type="text"
                      maxLength={6}
                      className="form-control text-center fs-3 mb-3"
                      placeholder="Enter 6 digit OTP"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                    />
                    <button
                      className="btn btn-success w-100"
                      disabled={loading || otp.length !== 6}
                      onClick={() => handleVerifyOtp()}
                    >
                      {loading ? "Verifying..." : "Verify Email"}
                    </button>

                    <div className="text-center mt-3">
                      {canResend ? (
                        <button
                          className="btn btn-link text-decoration-none"
                          disabled={loading}
                          onClick={handleResendOtp}
                        >
                          Resend OTP
                        </button>
                      ) : (
                        <span className="text-muted">
                          Resend OTP in {seconds}s
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center px-4">
                <div style={{ fontSize: 70 }}>✅</div>
                <h2 className="mt-3 text-success">Email Verified</h2>
                <p className="text-muted mt-3">
                  Your email has been verified successfully.
                </p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => (window.location.href = "/login")}
                >
                  Go To Login
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerification;
